import '.ef{max-width:none;}.ssr-plugin'
import '.eb{flex-shrink:0;}.ssr-plugin'
import '.eg{width:max-content;}.ssr-plugin'
import '.eh{display:inline-flex;}.ssr-plugin'
import '.ei{height:24px;}.ssr-plugin'
import '.ej{gap:2px;}.ssr-plugin'
import '.d{align-items:center;}.ssr-plugin'
import '.c{justify-content:center;}.ssr-plugin'
import '.ba{user-select:none;}.ssr-plugin'
import { forwardRef, ReactNode, Children, memo } from 'react'
import { colors } from '../../tokens/colors.js'
import { radius } from '../../utils/radius.js'
import { Icon, IconProps } from '../Icon/index.js'
import { Text } from '../Text/index.js'
import { getColorForString } from '../../utils/misc.js'
import { styled, Style } from 'inlines'

export type BadgeColors =
  | 'neutral-subtle'
  | 'neutral-fill'
  | 'red-subtle'
  | 'red-fill'
  | 'purple-subtle'
  | 'purple-fill'
  | 'green-subtle'
  | 'green-fill'
  | 'orange-subtle'
  | 'orange-fill'
  | 'white-subtle'
  | 'white-fill'
  | 'inverted-subtle'
  | 'inverted-fill'

type BadgeProps = {
  children: ReactNode
  color?: BadgeColors
  leadIcon?: IconProps['variant']
  trailIcon?: IconProps['variant']
  textVariant?: 'regular' | 'mono'
  style?: Style
}

const Badge = forwardRef<HTMLDivElement, BadgeProps>(
  (
    {
      children,
      color = typeof Children.toArray(children)[0] === 'string'
        ? `${getColorForString(Children.toArray(children)[0] as string)}-subtle`
        : 'neutral-subtle',
      leadIcon,
      trailIcon,
      textVariant = 'regular',
      style,
    },
    ref,
  ) => {
    const hasLeadIcon = !!leadIcon
    const hasTrailIcon = !!trailIcon

    let padding
    if (hasLeadIcon && hasTrailIcon) {
      padding = '0 4px'
    } else if (hasLeadIcon) {
      padding = '0 6px 0 4px'
    } else if (hasTrailIcon) {
      padding = '0 4px 0 6px'
    } else {
      padding = '0 6px'
    }

    return (
      <styled.div className="ef eb eg eh ei ej d c ba"
        ref={ref}
        style={{
          padding: padding,
          borderRadius: radius[8],
          ...(color === 'neutral-fill' && {
            background: colors.neutral100,
            color: colors.neutralInverted100,
          }),
          ...(color === 'red-fill' && {
            background: colors.red100,
            color: colors.white100,
          }),
          ...(color === 'green-fill' && {
            background: colors.green100,
            color: colors.white100,
          }),
          ...(color === 'purple-fill' && {
            background: colors.purple100,
            color: colors.white100,
          }),
          ...(color === 'orange-fill' && {
            background: colors.orange100,
            color: colors.white100,
          }),
          ...(color === 'neutral-subtle' && {
            background: colors.neutral20Adjusted,
            color: colors.neutral100,
          }),
          ...(color === 'red-subtle' && {
            background: colors.red20,
            color: colors.red100,
          }),
          ...(color === 'green-subtle' && {
            background: colors.green20,
            color: colors.green100,
          }),
          ...(color === 'purple-subtle' && {
            background: colors.purple20,
            color: colors.purple100,
          }),
          ...(color === 'orange-subtle' && {
            background: colors.orange20,
            color: colors.orange100,
          }),
          ...(color === 'inverted-subtle' && {
            background: colors.neutralInverted20,
            color: colors.neutral100,
          }),
          ...(color === 'inverted-fill' && {
            background: colors.neutralInverted100,
            color: colors.neutral100,
          }),
          ...(color === 'white-subtle' && {
            background: colors.white20,
            color: colors.white100,
          }),
          ...(color === 'white-fill' && {
            background: colors.white100,
            color: colors.black100,
          }),
          ...style,
        }}
      >
        {leadIcon && <Icon size="small" variant={leadIcon} color="inherit" />}
        {trailIcon && !leadIcon && <div style={{ width: 1, height: '100%' }} />}

        {typeof children !== 'object' || typeof children === 'string' ? (
          <Text
            singleLine
            color="inherit"
            variant={textVariant === 'mono' ? 'subtext-mono' : 'subtext-medium'}
          >
            {children}
          </Text>
        ) : (
          children
        )}
        {trailIcon && <Icon size="small" variant={trailIcon} color="inherit" />}
        {leadIcon && !trailIcon && <div style={{ width: 1, height: '100%' }} />}
      </styled.div>
    )
  },
)

const MemoizedBadge = memo(Badge)

export { MemoizedBadge as Badge }
export type { BadgeProps }
